import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { ErrorState, Icon, StateMessage } from "@/ds";
import { DominanciaBloco } from "../../dominancia-bloco";
import { JustificativaField } from "@/shared/components";
import tabela from "../../planejamento-controle.tabela.module.scss";
import {
  CENTRO_CUSTO_LABEL,
  CARREGANDO_PREVIA_LABEL,
  COLUNA_ACAO,
  COLUNA_CLASSIFICACAO,
  COLUNA_ESCOPO,
  COLUNA_PARAMETROS,
  COLUNA_PERIODO,
  COLUNA_SITUACAO,
  COLUNA_TOTAL,
  EMBARCACAO_LABEL,
  ESCOPOS_LABEL,
  ESCOPOS_TITLE,
  FINANCEIRO_TITLE,
  OBRA_LABEL,
  OUTLOOK_DESTINO,
  OUTLOOK_ORIGEM,
  REVISAO_TITLE,
  REVISAR_ARIA,
  REVISAR_LABEL,
  SAI_DE,
  TIPO_OBRA_LABEL,
  TOTAL_LABEL,
  VALOR_TIPO_DESTINO,
  VALOR_TIPO_ORIGEM,
  VAI_PARA,
} from "./revisao-transferencia-tipo.constants";
import modal from "../../planejamento-controle.modal.module.scss";
import styles from "./revisao-transferencia-tipo.module.scss";
import type {
  ContextoRevisao,
  DecisaoDominanciaRevisao,
  RevisaoTransferenciaTipoProps,
} from "./types-revisao-transferencia-tipo";

function Contexto({
  titulo,
  contexto,
  testId,
}: {
  titulo: string;
  contexto: ContextoRevisao;
  testId: string;
}) {
  return (
    <section className={styles.contexto} data-testid={testId}>
      <h4 className={styles.secaoTitulo}>{titulo}</h4>
      <dl className={styles.campos}>
        <div>
          <dt>{OBRA_LABEL}</dt>
          <dd>{contexto.obra}</dd>
        </div>
        <div>
          <dt>{EMBARCACAO_LABEL}</dt>
          <dd>{contexto.embarcacao}</dd>
        </div>
        <div>
          <dt>{TIPO_OBRA_LABEL}</dt>
          <dd>{contexto.tipoObra}</dd>
        </div>
        <div>
          <dt>{CENTRO_CUSTO_LABEL}</dt>
          <dd>{contexto.centroCusto}</dd>
        </div>
      </dl>
    </section>
  );
}

function Decisao({ decisao }: { decisao: DecisaoDominanciaRevisao }) {
  return (
    <>
      <DominanciaBloco {...decisao.bloco} />
      {decisao.justificativa ? (
        <JustificativaField
          labelClassName={modal.rotuloCampo}
          value={decisao.justificativa.value}
          onChange={decisao.justificativa.onChange}
          error={decisao.justificativa.error}
          disabled={false}
          rows={3}
          label={decisao.justificativa.label}
          testIds={decisao.justificativa.testIds}
        />
      ) : null}
    </>
  );
}

export function RevisaoTransferenciaTipoView({
  aviso,
  origem,
  destino,
  incorporacao,
  escopos,
  total,
  avisoImpedido,
  previa,
  erroRevisar,
  financeiro,
  dominanciaOrigem,
  dominanciaDestino,
}: RevisaoTransferenciaTipoProps) {
  return (
    <section className={styles.revisao} data-testid="transferir-tipo-revisao">
      <h3 className={styles.titulo}>{REVISAO_TITLE}</h3>
      <Message
        className="p-message-block"
        severity="info"
        text={aviso}
        data-testid="transferir-tipo-revisao-aviso"
      />

      <div className={styles.dePara}>
        <Contexto titulo={SAI_DE} contexto={origem} testId="transferir-tipo-sai-de" />
        <span className={styles.seta} aria-hidden>
          <i className="pi pi-arrow-right-arrow-left" />
        </span>
        <Contexto titulo={VAI_PARA} contexto={destino} testId="transferir-tipo-vai-para" />
      </div>
      <Message
        className="p-message-block"
        severity="info"
        text={incorporacao}
        data-testid="transferir-tipo-incorporacao"
      />

      <section data-testid="transferir-tipo-escopos">
        <h4 className={styles.secaoTitulo}>
          <Icon name="calendar" size={14} /> {ESCOPOS_TITLE}
        </h4>
        <div className={`${tabela.tabelaWrap} ${styles.tabelaEscopos}`}>
          <table className={`${tabela.tabela} ${styles.tabela}`} aria-label={ESCOPOS_LABEL}>
            <thead>
              <tr>
                <th scope="col" className={styles.colEscopo}>
                  {COLUNA_ESCOPO}
                </th>
                <th scope="col" className={styles.colSituacao}>
                  {COLUNA_SITUACAO}
                </th>
                <th scope="col">{COLUNA_PERIODO}</th>
                <th scope="col">{COLUNA_CLASSIFICACAO}</th>
                <th scope="col">{COLUNA_PARAMETROS}</th>
                <th scope="col">{COLUNA_TOTAL}</th>
                <th scope="col">{COLUNA_ACAO}</th>
              </tr>
            </thead>
            <tbody>
              {escopos.map((linha) => (
                <tr
                  key={linha.id}
                  className={linha.impedimentos.length > 0 ? styles.linhaImpedida : undefined}
                  data-testid={linha.testIds.linha}
                >
                  <td className={styles.colEscopo}>
                    {linha.nome}
                    {linha.impedimentos.length > 0 ? (
                      <ul className={styles.motivos} data-testid={linha.testIds.impedimentos}>
                        {linha.impedimentos.map((motivo) => (
                          <li key={motivo}>{motivo}</li>
                        ))}
                      </ul>
                    ) : null}
                    {linha.pendenciaDuracao ? (
                      <p className={styles.motivos} data-testid={linha.testIds.duracao}>
                        {linha.pendenciaDuracao}
                      </p>
                    ) : null}
                  </td>
                  <td className={styles.colSituacao}>
                    <Tag value={linha.situacao.texto} severity={linha.situacao.severity} />
                  </td>
                  <td>{linha.periodo}</td>
                  <td>
                    {linha.classificacao === "—" ? (
                      linha.classificacao
                    ) : (
                      <Tag value={linha.classificacao} severity="info" />
                    )}
                  </td>
                  <td>{linha.parametros}</td>
                  <td>{linha.total}</td>
                  <td>
                    <Button
                      type="button"
                      link
                      size="small"
                      className={`p-button-sm-flush ${styles.revisar}`}
                      pt={{ label: { className: styles.revisarRotulo } }}
                      label={REVISAR_LABEL}
                      aria-label={REVISAR_ARIA(linha.nome)}
                      loading={linha.revisar.carregando}
                      onClick={linha.revisar.onClick}
                      data-testid={linha.testIds.revisar}
                    />
                  </td>
                </tr>
              ))}
              <tr className={styles.total} data-testid="transferir-tipo-total">
                <td className={styles.colEscopo}>{TOTAL_LABEL}</td>
                <td className={styles.colSituacao} />
                <td colSpan={3} />
                <td data-testid="transferir-tipo-total-valor">{total.total}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
        {previa.carregando ? (
          <StateMessage
            variant="loading"
            title={CARREGANDO_PREVIA_LABEL}
            testId="transferir-tipo-previa-carregando"
          />
        ) : null}
        {previa.erro ? (
          <ErrorState
            message={previa.erro}
            onRetry={previa.recarregar}
            testId="transferir-tipo-previa-erro"
          />
        ) : null}
        {erroRevisar ? (
          <Message
            className="p-message-block"
            severity="error"
            text={erroRevisar}
            data-testid="transferir-tipo-revisar-erro"
          />
        ) : null}
        {avisoImpedido ? (
          <Message
            className="p-message-block"
            severity="warn"
            text={avisoImpedido}
            data-testid="transferir-tipo-impedido-aviso"
          />
        ) : null}
      </section>

      <section data-testid="transferir-tipo-financeiro">
        <h4 className={styles.secaoTitulo}>{FINANCEIRO_TITLE}</h4>
        <dl className={styles.financeiro}>
          <div className={styles.contexto}>
            <dt>{OUTLOOK_ORIGEM}</dt>
            <dd data-testid="transferir-tipo-outlook-origem">{financeiro.outlookOrigem}</dd>
            <dt>{VALOR_TIPO_ORIGEM}</dt>
            <dd data-testid="transferir-tipo-valor-origem">{financeiro.valorTipoOrigem}</dd>
          </div>
          <div className={styles.contexto}>
            <dt>{OUTLOOK_DESTINO}</dt>
            <dd data-testid="transferir-tipo-outlook-destino">{financeiro.outlookDestino}</dd>
            <dt>{VALOR_TIPO_DESTINO}</dt>
            <dd data-testid="transferir-tipo-valor-destino">{financeiro.valorTipoDestino}</dd>
          </div>
        </dl>
      </section>

      {dominanciaOrigem ? <Decisao decisao={dominanciaOrigem} /> : null}
      {dominanciaDestino ? <Decisao decisao={dominanciaDestino} /> : null}
    </section>
  );
}
